import {
  Box,
  Divider,
  MenuItem,
  MenuList,
  Popover,
  PopoverOrigin,
  PopoverVirtualElement,
  Stack,
  SxProps,
  Theme,
  Typography,
  useColorScheme,
  useTheme,
} from "@mui/material";
import { useMatch, useNavigate, useResolvedPath } from "react-router";
import { useGlobalContext } from "../../stores/Global";
import { useTranslation } from "react-i18next";
import useBreakpoint from "../../hooks/useBreakpoint";
import { NotificationService } from "../../services/api";
import LanguageSwitcher from "./LanguageSwitcher";
import FormInput from "./FormInput";

interface AccountPopoverProps {
  anchorEl?:
    | Element
    | (() => Element)
    | PopoverVirtualElement
    | (() => PopoverVirtualElement)
    | null
    | undefined;
  open: boolean;
  onClose?: (closeSidebar?: boolean) => any;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  sx?: SxProps<Theme>;
}

export default function AccountPopover(props: AccountPopoverProps) {
  const { t } = useTranslation("Navigation");
  const { context, setContext } = useGlobalContext();
  const breakpoint = useBreakpoint();
  const theme = useTheme();
  const { setMode } = useColorScheme();
  const navigate = useNavigate();
  const match = useMatch("/account/:page/*");
  const isHome = useResolvedPath("").pathname === "/home";

  const page = match?.params.page;

  const getMenuItemColor = (targetPage?: string) => {
    if (targetPage === page) return theme.palette.primary.light;
    else return theme.palette.text.primary;
  };

  const navigateToPage = (
    targetPage: "info" | "my-tickets" | "forgot-password"
  ) => {
    if (targetPage === "my-tickets") navigate(`/${targetPage}`);
    else navigate(`/account/${targetPage}`);
    if (!breakpoint.md) setContext({ ...context, sidebar: false });
    props.onClose?.(true);
  };

  const logout = async () => {
    if (!context.user) return;
    await NotificationService.deleteSubscription();
    localStorage.removeItem("authToken");
    setContext({
      ...context,
      user: undefined,
      sidebar: breakpoint.md ? context.sidebar : false,
    });
    navigate("/home");
    props.onClose?.(true);
  };

  const handleToggleDarkTheme = () => {
    setMode(theme.palette.mode === "dark" ? "light" : "dark");
  };

  return (
    <Popover
      open={props.open}
      onClose={() => props.onClose?.()}
      anchorEl={props.anchorEl}
      anchorOrigin={props.anchorOrigin}
      transformOrigin={props.transformOrigin}
      container={document.body}
      sx={{ ...props.sx, zIndex: 9999999 }}
    >
      <MenuList sx={{ minWidth: 150, position: "relative" }}>
        {!isHome && (
          <MenuItem
            onClick={() => {
              navigate("/home");
              props.onClose?.(true);
            }}
          >
            {t("Home")}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => navigateToPage("my-tickets")}
          sx={{ color: getMenuItemColor("my-tickets") }}
        >
          {t("MyTickets")}
        </MenuItem>
        <MenuItem
          onClick={() => navigateToPage("info")}
          sx={{ color: getMenuItemColor("info") }}
        >
          {t("AccountInfo")}
        </MenuItem>
        <MenuItem
          onClick={() => navigateToPage("forgot-password")}
          sx={{ color: getMenuItemColor("forgot-password") }}
        >
          {context.user ? t("ChangePassword") : t("ForgotPassword")}
        </MenuItem>
        <MenuItem sx={{ width: 1 }} onClick={handleToggleDarkTheme}>
          <Typography>
            {theme.palette.mode === "dark" ? t("LightTheme") : t("DarkTheme")}
          </Typography>
        </MenuItem>
        <LanguageSwitcher />
        <Divider />
        <MenuItem onClick={logout}> {t("Logout")}</MenuItem>
      </MenuList>
    </Popover>
  );
}
